import { toolTipForTable } from 'utils';
import Strings from '../../Strings';
import { VITALS } from '../../utils/readings';
import {
  convertKilogramToPound,
  GlucoseUnitConverter,
  GLUCOSE_UNITS,
  roundToDecimal,
  convertCelsiusToFahrenheit,
  TEMPERATURE_UNITS,
} from '../../utils/unitConverters';

export const eventTypeRenderer = eventType => {
  switch (eventType) {
    case 'weight':
      return Strings.weight;
    case 'blood_pressure':
      return Strings.bloodPressure;
    case 'glucose':
      return Strings.glucose;
    case 'spirometer':
      return Strings.spirometry;
    case 'temperature':
      return Strings.temperature;
    case 'saturation':
      return Strings.oxygenSaturation;
    case 'heart_rate':
      return Strings.heartRate;
    case 'air_quality':
      return Strings.airQuality;
    case 'coagulation':
      return Strings.coagulation;
    default:
      return eventType;
  }
};

export const typeRenderer = type => {
  switch (type) {
    case 'measurements':
      return Strings.measurements;
    case 'takes':
      return Strings.missed;
    case 'battery':
      return Strings.battery.battery;
    case 'schedule_missed_doses':
      return Strings.missedDoses;
    case 'enrollment':
      return Strings.enrollment;
    default:
      return 'Unknown';
  }
};

export const priorityRenderer = priority => {
  switch (priority) {
    case 0:
      return 'Low';
    case 1:
      return 'Medium';
    case 2:
      return 'High';
    default:
      return 'Unknown';
  }
};

export const idRenderer = e => {
  let prefix = '';
  switch (e.event_type) {
    case 'weight':
      prefix = 'SCALE';
      break;
    case 'blood_pressure':
      prefix = 'BPM';
      break;
    case 'glucose':
      prefix = 'GLU';
      break;
    case 'spirometer':
      prefix = 'SPI';
      break;
    case 'temperature':
      prefix = 'TEMP';
      break;
    case 'saturation':
      prefix = 'SAT';
      break;
    case 'heart_rate':
      prefix = 'HR';
      break;
    case 'air_quality':
      prefix = 'AIRQ';
      break;
    case 'coagulation':
      prefix = 'COAG';
      break;
    default:
      prefix = 'UNKNOWN';
  }
  if (e.general_type === 'schedule_missed_doses') {
    prefix = 'DOSES';
  }
  if (e.general_type === 'enrollment') {
    prefix = 'ENROLL';
  }
  return `${prefix}${e.id}`;
};

export const unitRenderer = unit => {
  switch (unit) {
    case 'kilogram, meter':
      return 'kg';
    case 'punds, inch':
      return 'lbs';
    default:
      return 'Unknown';
  }
};

export const getAlertsValue = (conditions, extended_items) => {
  const result = [];
  conditions.forEach(condition => {
    if (condition.field === VITALS.WEIGHT) {
      const diff_in_lbs = roundToDecimal(convertKilogramToPound(condition.diff), 1);
      if (condition.actual_value > condition.max) {
        result.push(`+ ${diff_in_lbs} lbs`);
      } else if (condition.actual_value < condition.min) {
        result.push(`- ${diff_in_lbs} lbs`);
      }
    } else if (condition.field === VITALS.PRESSURE_SYSTOLIC) {
      result.push(`SYS ${condition.actual_value}`);
    } else if (condition.field === VITALS.PRESSURE_DIASTOLIC) {
      result.push(`DIA ${condition.actual_value}`);
    } else if (condition.field === VITALS.FORCED_EXPIRATORY_VOLUME_1S) {
      result.push(`FEV1 ${condition.actual_value}`);
    } else if (condition.field === VITALS.FORCED_VITAL_CAPACITY) {
      result.push(`FVC ${condition.actual_value}`);
    } else if (condition.field === VITALS.SUGAR_LEVEL) {
      const actual_value = roundToDecimal(
        GlucoseUnitConverter(condition.actual_value, condition.unit, GLUCOSE_UNITS.mg_dl),
        1,
      );
      result.push(`${actual_value} ${GLUCOSE_UNITS.mg_dl}`);
    } else if (condition.field === VITALS.TEMPERATURE) {
      const actual_value = roundToDecimal(convertCelsiusToFahrenheit(condition.actual_value), 1);
      result.push(`TEMP ${actual_value} ${TEMPERATURE_UNITS.f}`);
    } else if (condition.field === VITALS.OXYGEN_SATURATION) {
      result.push(`SAT ${condition.actual_value}%`);
    } else if (condition.field === VITALS.HEART_RATE) {
      result.push(`HR ${condition.actual_value} ${Strings.bpm_unit}`);
    } else if (condition.field === 'missed_doses') {
      result.push(extended_items?.join(', ') || '-');
    } else if (condition.field === VITALS.PM25) {
      result.push(`PM 2.5 ${condition.actual_value} µg/m³`);
    } else if (condition.field === VITALS.PROTHROMBIN_TIME) {
      result.push(`PT ${condition.actual_value}s`);
    } else if (condition.field === VITALS.INTERNATIONAL_NORMALIZED_RATIO) {
      result.push(`INR ${condition.actual_value}`);
    } else if (condition.field === 'missing_mandatory_tasks') {
      result.push(Strings.missingMandatoryTasks);
    } else if (condition.field === 'missing_cpt') {
      result.push(`${Strings.missingCPTCodes}: ${extended_items?.join(', ') || '-'}`);
    }
  });
  return { value: result.join(' '), tooltip: extended_items };
};
